import { selectCollectionSummaries, useAppSelector, useT } from "@reciperoll/core";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Folder, FolderPlus } from "react-feather";
import { SectionHeader } from "./SectionHeader";
import { ScrollRail } from "./ScrollRail";
import { CollectionForm } from "../CollectionForm";
import { StorageImage } from "../StorageImage";

// Home rail of the user's collections. Deliberately styled differently from the
// recipe rails — wide landscape cards with the name/count overlaid on the image —
// so a "collection" reads as a container, not another recipe.
export default function Collections() {
  const t = useT();
  const navigate = useNavigate();
  const collections = useAppSelector(selectCollectionSummaries);
  const withRecipes = collections.filter((c) => c.recipeCount > 0);
  const [showForm, setShowForm] = useState(false);

  if (!withRecipes.length) return null;

  return (
    <div className="mb-8">
      <SectionHeader
        title={t("nav.collections")}
        icon={<Folder className="w-5 h-5" />}
        link="/collections"
      />
      <ScrollRail className="flex gap-4 pb-2 -mx-1 px-1">
        {withRecipes.map((collection) => (
          <Link
            key={collection.id}
            to={`/collection/${collection.id}`}
            className="group relative block shrink-0 w-72 h-40 rounded-2xl overflow-hidden bg-surface-2"
          >
            <StorageImage
              stored={collection.resolvedImage}
              imgClassName="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              fallback={
                <div className="absolute inset-0 bg-terracotta flex items-center justify-center">
                  <Folder className="w-11 h-11 text-white/45" />
                </div>
              }
            />

            {/* Bottom scrim so the overlaid text stays legible on any photo. */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent" />

            <div className="absolute inset-x-0 bottom-0 p-4">
              <div className="flex items-center gap-1.5 mb-1">
                <Folder className="w-3 h-3 text-white/85" />
                <span className="text-[11px] font-bold uppercase tracking-wider text-white/85">
                  {t("library.recipeCount", { count: collection.recipeCount })}
                </span>
              </div>
              <h3 className="text-white text-xl font-bold truncate">{collection.name}</h3>
            </div>
          </Link>
        ))}

        {/* A low-key "create" affordance at the end of the rail — fills the
            space when you only have a collection or two, doubles as a hint about
            what collections are for, and opens the create modal directly. */}
        <button
          onClick={() => setShowForm(true)}
          className="shrink-0 w-72 h-40 rounded-2xl border-2 border-dashed border-line bg-terracotta-wash flex flex-col items-center justify-center px-6 hover:border-terracotta transition-colors cursor-pointer"
        >
          <span className="w-11 h-11 rounded-full bg-surface flex items-center justify-center mb-2">
            <FolderPlus className="w-5 h-5 text-terracotta" />
          </span>
          <span className="text-base font-bold text-terracotta text-center">
            {t("collections.newCollection")}
          </span>
          <span className="text-xs text-ink-faint text-center mt-0.5">
            {t("collections.createHint")}
          </span>
        </button>
      </ScrollRail>

      {showForm && (
        <CollectionForm
          onClose={() => setShowForm(false)}
          onCreated={(id) => navigate(`/collection/${id}`)}
        />
      )}
    </div>
  );
}
